import { type Key, useState } from 'react';
import {
  Focusable,
  IconButton,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { Scopes } from '../../auth';
import type { Account, Member, SamlConfig } from '../model';

import { getResetMfaActionState } from './resetMfa';
import { ResetMfaDrawer } from './ResetMfaDrawer';

type MemberActionsMenuProps = Readonly<{
  member: Member;
  account: Account;
  samlConfig?: SamlConfig;
  scopes: Scopes;
  onMembersRefresh: () => void;
  marketplaceProvider?: string;
}>;

export function MemberActionsMenu({
  member,
  account,
  samlConfig,
  scopes,
  onMembersRefresh,
  marketplaceProvider,
}: MemberActionsMenuProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Only show the menu if the user has the memberMfaReset scope
  if (!scopes.memberMfaReset) {
    return null;
  }

  const actionState = getResetMfaActionState(member, samlConfig);

  // No actions available (e.g., pending invite)
  if (actionState === null) {
    return null;
  }

  const handleAction = (key: Key) => {
    if (key === 'reset-mfa' && actionState.available) {
      setIsDrawerOpen(true);
    }
  };

  return (
    <>
      <MenuTrigger>
        <IconButton aria-label={`Actions for ${member.email}`} variant="minimal" icon="more-horiz" size="small" />
        <Popover placement="bottom end">
          <Menu onAction={handleAction}>
            {actionState.available ? (
              <MenuItem id="reset-mfa" variant="destructive">
                <Text slot="label">
                  <Icon name="sync" size="small" /> Reset MFA
                </Text>
              </MenuItem>
            ) : (
              <TooltipTrigger>
                <Focusable>
                  <MenuItem id="reset-mfa" isDisabled>
                    <Text slot="label">
                      <Icon name="sync" size="small" /> Reset MFA
                    </Text>
                  </MenuItem>
                </Focusable>
                <Tooltip placement="left">{actionState.reason}</Tooltip>
              </TooltipTrigger>
            )}
          </Menu>
        </Popover>
      </MenuTrigger>
      {isDrawerOpen && (
        <ResetMfaDrawer
          isOpen
          member={member}
          account={account}
          onClose={() => setIsDrawerOpen(false)}
          onSuccess={onMembersRefresh}
          marketplaceProvider={marketplaceProvider}
        />
      )}
    </>
  );
}
